import { useEffect, useRef, useState } from "react";
import { useSiteSettings, defaultSettings, type SiteSettings } from "@/hooks/useSiteSettings";
import AdSettingsPanel from "@/pages/admin/settings/AdSettingsPanel";
import ConfirmDialog from "@/pages/admin/components/ConfirmDialog";
import { clearDraft, readDraft, writeDraft } from "@/utils/adminDraft";

const PRESET_PALETTES = [
  { name: "Laranja (Padrão)", primary: "#f97316", secondary: "#1c1917", accent: "#ea580c" },
  { name: "Vermelho Vibrante", primary: "#ef4444", secondary: "#1a1a1a", accent: "#dc2626" },
  { name: "Verde Esmeralda", primary: "#10b981", secondary: "#064e3b", accent: "#059669" },
  { name: "Rosa Moderno", primary: "#ec4899", secondary: "#1f1f2e", accent: "#db2777" },
  { name: "Âmbar Dourado", primary: "#f59e0b", secondary: "#1c1917", accent: "#d97706" },
  { name: "Ciano Tech", primary: "#06b6d4", secondary: "#0f172a", accent: "#0891b2" },
];

const INITIAL_MENU_PRESETS = [
  { name: "Hero Night", background: "#1c1917", text: "#fdba74", activeText: "#fb923c" },
  { name: "Hero Warm", background: "#7c2d12", text: "#ffedd5", activeText: "#fdba74" },
  { name: "Hero Sunset", background: "#431407", text: "#fed7aa", activeText: "#fb923c" },
];

const FIXED_MENU_PRESETS = [
  { name: "Hero Clean", background: "#ffffff", text: "#334155", activeText: "#fb923c" },
  { name: "Hero Warm", background: "#fff7ed", text: "#7c2d12", activeText: "#f97316" },
  { name: "Hero Night", background: "#1c1917", text: "#fdba74", activeText: "#fb923c" },
];

type TabKey = "identidade" | "cores" | "social" | "seo" | "monetizacao";

const SITE_SETTINGS_DRAFT_STORAGE_KEY = "afiliabr_admin_site_settings_draft_v1";

function areSettingsEqual(first: SiteSettings, second: SiteSettings) {
  return JSON.stringify(first) === JSON.stringify(second);
}

export default function AdminSettings() {
  const { settings, updateSettings, resetToDefault } = useSiteSettings();
  const storedDraft = readDraft<SiteSettings>(SITE_SETTINGS_DRAFT_STORAGE_KEY);
  const [activeTab, setActiveTab] = useState<TabKey>("identidade");
  const [draft, setDraft] = useState<SiteSettings>(storedDraft ?? { ...settings });
  const [saved, setSaved] = useState(false);
  const [logoPreviewError, setLogoPreviewError] = useState(false);
  const [draftRestored, setDraftRestored] = useState(Boolean(storedDraft));
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const hasUnsavedChanges = !areSettingsEqual(draft, settings);

  useEffect(() => {
    if (draftRestored) return;
    setDraft({ ...settings });
  }, [draftRestored, settings]);

  useEffect(() => {
    writeDraft(SITE_SETTINGS_DRAFT_STORAGE_KEY, draft, () => hasUnsavedChanges);
  }, [draft, hasUnsavedChanges]);

  const handleChange = <TField extends keyof SiteSettings>(field: TField, value: SiteSettings[TField]) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSocialChange = (platform: keyof SiteSettings["socialLinks"], value: string) => {
    setDraft((prev) => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [platform]: value },
    }));
    setSaved(false);
  };

  const handleApplyPalette = (palette: typeof PRESET_PALETTES[0]) => {
    setDraft((prev) => ({
      ...prev,
      primaryColor: palette.primary,
      secondaryColor: palette.secondary,
      accentColor: palette.accent,
    }));
    setSaved(false);
  };

  const handleApplyInitialMenuPreset = (preset: typeof INITIAL_MENU_PRESETS[0]) => {
    setDraft((prev) => ({
      ...prev,
      initialMenuBackgroundColor: preset.background,
      initialMenuTextColor: preset.text,
      initialMenuActiveTextColor: preset.activeText,
    }));
    setSaved(false);
  };

  const handleApplyFixedMenuPreset = (preset: typeof FIXED_MENU_PRESETS[0]) => {
    setDraft((prev) => ({
      ...prev,
      fixedMenuBackgroundColor: preset.background,
      fixedMenuTextColor: preset.text,
      fixedMenuActiveTextColor: preset.activeText,
    }));
    setSaved(false);
  };

  const handleSave = () => {
    updateSettings(draft);
    clearDraft(SITE_SETTINGS_DRAFT_STORAGE_KEY);
    setDraftRestored(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = () => {
    setConfirmResetOpen(true);
  };

  const confirmReset = () => {
    resetToDefault();
    clearDraft(SITE_SETTINGS_DRAFT_STORAGE_KEY);
    setDraft({ ...defaultSettings });
    setDraftRestored(false);
    setSaved(false);
    setConfirmResetOpen(false);
    setToast("Configurações Restaurada");
    setTimeout(() => setToast(null), 3000);
  };

  const discardDraft = () => {
    clearDraft(SITE_SETTINGS_DRAFT_STORAGE_KEY);
    setDraft({ ...settings });
    setDraftRestored(false);
    setSaved(false);
  };

  const tabs: { key: TabKey; icon: string; label: string }[] = [
    { key: "identidade", icon: "ri-store-2-line", label: "Identidade" },
    { key: "cores", icon: "ri-palette-line", label: "Cores & Tema" },
    { key: "social", icon: "ri-share-line", label: "Redes Sociais" },
    { key: "seo", icon: "ri-search-eye-line", label: "SEO" },
    { key: "monetizacao", icon: "ri-money-dollar-circle-line", label: "Monetização" },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configurações do Site</h1>
          <p className="text-sm text-gray-500 mt-1">Personalize a identidade visual e informações do site sem tocar no código</p>
        </div>
        <div className="flex items-center gap-3">
          {hasUnsavedChanges && (
            <button
              onClick={discardDraft}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line mr-1.5"></i>
              Descartar Rascunho
            </button>
          )}
          <button
            onClick={handleReset}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1.5"></i>
            Restaurar Padrão
          </button>
          <button
            onClick={handleSave}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              saved
                ? "bg-green-500 text-white"
                : "bg-orange-500 hover:bg-orange-600 text-white"
            }`}
          >
            <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
            {saved ? "Salvo!" : "Salvar Alterações"}
          </button>
        </div>
      </div>

      {draftRestored && (
        <div className="mb-6 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          Rascunho das configurações restaurado automaticamente. Você pode continuar editando e salvar quando estiver pronto.
        </div>
      )}

      <div className="flex gap-6">
        {/* Sidebar Tabs */}
        <div className="w-48 flex-shrink-0">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap text-left ${
                  activeTab === tab.key
                    ? "bg-orange-50 text-orange-600 border border-orange-100"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${tab.icon} text-base`}></i>
                </div>
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Live Preview Card */}
          <div className="mt-6 p-4 rounded-xl border border-gray-100 bg-white">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Preview</p>
            <div
              className="rounded-lg p-3 text-white text-center"
              style={{ backgroundColor: draft.primaryColor }}
            >
              {draft.logoUrl && !logoPreviewError ? (
                <img
                  src={draft.logoUrl}
                  alt="Logo preview"
                  className="h-6 w-auto object-contain mx-auto mb-1 brightness-0 invert"
                  onError={() => setLogoPreviewError(true)}
                />
              ) : (
                <span className="text-sm font-bold">{draft.siteName}</span>
              )}
            </div>
            <div className="mt-3 flex gap-2">
              <div
                className="flex-1 h-6 rounded-md"
                style={{ backgroundColor: draft.primaryColor }}
                title="Cor Primária"
              ></div>
              <div
                className="flex-1 h-6 rounded-md"
                style={{ backgroundColor: draft.secondaryColor }}
                title="Cor Secundária"
              ></div>
              <div
                className="flex-1 h-6 rounded-md"
                style={{ backgroundColor: draft.accentColor }}
                title="Cor de Destaque"
              ></div>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">{draft.siteName}</p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 bg-white rounded-2xl border border-gray-100 p-8">
          {/* IDENTIDADE */}
          {activeTab === "identidade" && (
            <div className="space-y-6">
              <SectionTitle icon="ri-store-2-line" title="Identidade do Site" />

              <div className="grid grid-cols-2 gap-5">
                <FormField label="Nome do Site" required>
                  <input
                    type="text"
                    value={draft.siteName}
                    onChange={(e) => handleChange("siteName", e.target.value)}
                    placeholder="Ex: AfiliaBR"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </FormField>

                <FormField label="Slogan / Tagline">
                  <input
                    type="text"
                    value={draft.slogan}
                    onChange={(e) => handleChange("slogan", e.target.value)}
                    placeholder="Ex: Os melhores produtos com os melhores preços"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </FormField>
              </div>

              <FormField label="URL do Logo" hint="Cole a URL de uma imagem PNG ou SVG (fundo transparente recomendado)">
                <div className="flex gap-3">
                  <input
                    ref={logoInputRef}
                    type="url"
                    value={draft.logoUrl}
                    onChange={(e) => {
                      handleChange("logoUrl", e.target.value);
                      setLogoPreviewError(false);
                    }}
                    placeholder="https://seusite.com/logo.png"
                    className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                  />
                  {draft.logoUrl && (
                    <div className="w-24 h-10 rounded-lg border border-gray-200 flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
                      {!logoPreviewError ? (
                        <img
                          src={draft.logoUrl}
                          alt="Preview"
                          className="h-8 w-auto object-contain"
                          onError={() => setLogoPreviewError(true)}
                        />
                      ) : (
                        <span className="text-xs text-red-400">Erro</span>
                      )}
                    </div>
                  )}
                </div>
                {logoPreviewError && (
                  <p className="text-xs text-red-500 mt-1">
                    <i className="ri-error-warning-line mr-1"></i>
                    URL inválida ou imagem não carregou. Verifique o endereço.
                  </p>
                )}
              </FormField>

              <FormField label="URL do Favicon" hint="Ícone que aparece na aba do navegador (32x32px recomendado)">
                <input
                  type="url"
                  value={draft.faviconUrl}
                  onChange={(e) => handleChange("faviconUrl", e.target.value)}
                  placeholder="https://seusite.com/favicon.ico"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                />
              </FormField>

              <div className="border-t border-gray-100 pt-6">
                <SectionTitle icon="ri-copyright-line" title="Rodapé" />
                <FormField label="Texto de Copyright" hint="Aparece na parte inferior do site">
                  <input
                    type="text"
                    value={draft.footerText}
                    onChange={(e) => handleChange("footerText", e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </FormField>
              </div>
            </div>
          )}

          {/* CORES */}
          {activeTab === "cores" && (
            <div className="space-y-6">
              <SectionTitle icon="ri-palette-line" title="Cores & Tema" />

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3">Paletas Prontas</p>
                <div className="grid grid-cols-3 gap-3">
                  {PRESET_PALETTES.map((palette) => (
                    <button
                      key={palette.name}
                      onClick={() => handleApplyPalette(palette)}
                      className="group relative p-3 rounded-xl border border-gray-100 hover:border-orange-200 transition-all cursor-pointer text-left"
                    >
                      <div className="flex gap-1.5 mb-2">
                        <div className="w-6 h-6 rounded-full" style={{ backgroundColor: palette.primary }}></div>
                        <div className="w-6 h-6 rounded-full" style={{ backgroundColor: palette.secondary }}></div>
                        <div className="w-6 h-6 rounded-full" style={{ backgroundColor: palette.accent }}></div>
                      </div>
                      <p className="text-xs font-medium text-gray-700">{palette.name}</p>
                      <div className="absolute inset-0 rounded-xl border-2 border-orange-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                      {draft.primaryColor === palette.primary && draft.secondaryColor === palette.secondary && (
                        <div className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded-full bg-orange-500">
                          <i className="ri-check-line text-white text-xs"></i>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <p className="text-sm font-semibold text-gray-700 mb-4">Personalizar Cores Manualmente</p>
                <div className="grid grid-cols-3 gap-5">
                  <ColorPicker
                    label="Cor Primária"
                    hint="Botões, links, destaques"
                    value={draft.primaryColor}
                    onChange={(v) => handleChange("primaryColor", v)}
                  />
                  <ColorPicker
                    label="Cor Secundária"
                    hint="Fundo do rodapé, sidebar"
                    value={draft.secondaryColor}
                    onChange={(v) => handleChange("secondaryColor", v)}
                  />
                  <ColorPicker
                    label="Cor de Destaque"
                    hint="Hover, badges, ícones"
                    value={draft.accentColor}
                    onChange={(v) => handleChange("accentColor", v)}
                  />
                </div>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <p className="text-sm font-semibold text-gray-700 mb-3">Menu Inicial / Inativo</p>
                <p className="text-sm text-gray-500 mb-4">Defina as cores do menu antes de ele entrar no estado ativo/fixo. O preset inicial já começa com Hero Night.</p>

                <div className="grid grid-cols-3 gap-3 mb-5">
                  {INITIAL_MENU_PRESETS.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => handleApplyInitialMenuPreset(preset)}
                      className="group relative p-3 rounded-xl border border-gray-100 hover:border-orange-200 transition-all cursor-pointer text-left"
                    >
                      <div className="rounded-lg border border-gray-100 overflow-hidden mb-2">
                        <div className="h-7 px-2 flex items-center gap-1.5" style={{ backgroundColor: preset.background }}>
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: preset.activeText }}></span>
                          <span className="text-[10px] font-semibold" style={{ color: preset.text }}>Menu</span>
                          <span className="text-[10px] font-semibold" style={{ color: preset.activeText }}>Ativo</span>
                        </div>
                      </div>
                      <p className="text-xs font-medium text-gray-700">{preset.name}</p>
                      <div className="flex gap-1.5 mt-2">
                        <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: preset.background }}></div>
                        <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: preset.text }}></div>
                        <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: preset.activeText }}></div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-5">
                  <ColorPicker
                    label="Fundo do Menu Inativo"
                    hint="Cor do fundo antes do menu entrar no estado fixo"
                    value={draft.initialMenuBackgroundColor}
                    onChange={(v) => handleChange("initialMenuBackgroundColor", v)}
                  />
                  <ColorPicker
                    label="Texto do Menu Inativo"
                    hint="Cor padrão dos links e ícones no estado inicial"
                    value={draft.initialMenuTextColor}
                    onChange={(v) => handleChange("initialMenuTextColor", v)}
                  />
                  <ColorPicker
                    label="Texto Selecionado Inativo"
                    hint="Cor do item ativo no menu inicial"
                    value={draft.initialMenuActiveTextColor}
                    onChange={(v) => handleChange("initialMenuActiveTextColor", v)}
                  />
                </div>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <p className="text-sm font-semibold text-gray-700 mb-3">Menu Superior Fixo</p>
                <p className="text-sm text-gray-500 mb-4">Defina o fundo, a cor do texto e a cor do item selecionado quando o menu estiver fixo no topo.</p>

                <div className="grid grid-cols-3 gap-3 mb-5">
                  {FIXED_MENU_PRESETS.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => handleApplyFixedMenuPreset(preset)}
                      className="group relative p-3 rounded-xl border border-gray-100 hover:border-orange-200 transition-all cursor-pointer text-left"
                    >
                      <div className="rounded-lg border border-gray-100 overflow-hidden mb-2">
                        <div className="h-7 px-2 flex items-center gap-1.5" style={{ backgroundColor: preset.background }}>
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: preset.activeText }}></span>
                          <span className="text-[10px] font-semibold" style={{ color: preset.text }}>Menu</span>
                          <span className="text-[10px] font-semibold" style={{ color: preset.activeText }}>Ativo</span>
                        </div>
                      </div>
                      <p className="text-xs font-medium text-gray-700">{preset.name}</p>
                      <div className="flex gap-1.5 mt-2">
                        <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: preset.background }}></div>
                        <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: preset.text }}></div>
                        <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: preset.activeText }}></div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-5">
                  <ColorPicker
                    label="Fundo do Menu Fixo"
                    hint="Cor do fundo quando a navbar estiver fixa"
                    value={draft.fixedMenuBackgroundColor}
                    onChange={(v) => handleChange("fixedMenuBackgroundColor", v)}
                  />
                  <ColorPicker
                    label="Texto do Menu Fixo"
                    hint="Cor padrão dos links e ícones"
                    value={draft.fixedMenuTextColor}
                    onChange={(v) => handleChange("fixedMenuTextColor", v)}
                  />
                  <ColorPicker
                    label="Texto Selecionado"
                    hint="Cor do item ativo/selecionado no menu"
                    value={draft.fixedMenuActiveTextColor}
                    onChange={(v) => handleChange("fixedMenuActiveTextColor", v)}
                  />
                </div>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <p className="text-sm font-semibold text-gray-700 mb-4">Preview de Componentes</p>
                <div className="rounded-xl border border-gray-100 p-5 space-y-4 bg-gray-50">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Menu inativo</p>
                    <div
                      className="rounded-lg px-5 py-3 flex items-center justify-between"
                      style={{ backgroundColor: draft.initialMenuBackgroundColor }}
                    >
                      <span className="font-bold text-sm" style={{ color: draft.initialMenuActiveTextColor }}>{draft.siteName}</span>
                      <div className="flex gap-3">
                        <span className="text-xs font-semibold" style={{ color: draft.initialMenuTextColor }}>Ofertas</span>
                        <span className="text-xs font-semibold" style={{ color: draft.initialMenuActiveTextColor }}>Reviews</span>
                        <span className="text-xs font-semibold" style={{ color: draft.initialMenuTextColor }}>Guias</span>
                      </div>
                      <button
                        className="px-3 py-1.5 rounded-lg text-white text-xs font-semibold whitespace-nowrap cursor-pointer"
                        style={{ backgroundColor: draft.primaryColor }}
                      >
                        Ver Ofertas
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Menu fixo</p>
                    <div
                      className="rounded-lg px-5 py-3 flex items-center justify-between"
                      style={{ backgroundColor: draft.fixedMenuBackgroundColor }}
                    >
                      <span className="font-bold text-sm" style={{ color: draft.fixedMenuActiveTextColor }}>{draft.siteName}</span>
                      <div className="flex gap-3">
                        <span className="text-xs font-semibold" style={{ color: draft.fixedMenuTextColor }}>Ofertas</span>
                        <span className="text-xs font-semibold" style={{ color: draft.fixedMenuActiveTextColor }}>Reviews</span>
                        <span className="text-xs font-semibold" style={{ color: draft.fixedMenuTextColor }}>Guias</span>
                      </div>
                      <button
                        className="px-3 py-1.5 rounded-lg text-white text-xs font-semibold whitespace-nowrap cursor-pointer"
                        style={{ backgroundColor: draft.primaryColor }}
                      >
                        Ver Ofertas
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <button
                      className="px-4 py-2 rounded-lg text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
                      style={{ backgroundColor: draft.primaryColor }}
                    >
                      Botão Primário
                    </button>
                    <button
                      className="px-4 py-2 rounded-lg text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
                      style={{ backgroundColor: draft.accentColor }}
                    >
                      Botão Destaque
                    </button>
                    <span
                      className="px-3 py-1 rounded-full text-white text-xs font-bold"
                      style={{ backgroundColor: draft.primaryColor }}
                    >
                      Badge
                    </span>
                    <span
                      className="px-3 py-1 rounded-full text-xs font-bold border"
                      style={{ color: draft.primaryColor, borderColor: draft.primaryColor }}
                    >
                      Outline
                    </span>
                  </div>

                  <div
                    className="rounded-lg px-5 py-3 text-center"
                    style={{ backgroundColor: draft.secondaryColor }}
                  >
                    <p className="text-white/50 text-xs">{draft.footerText}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SOCIAL */}
          {activeTab === "social" && (
            <div className="space-y-6">
              <SectionTitle icon="ri-share-line" title="Redes Sociais" />
              <p className="text-sm text-gray-500">Configure os links das redes sociais que aparecem no rodapé do site.</p>

              <div className="space-y-4">
                {[
                  { key: "instagram" as const, icon: "ri-instagram-line", label: "Instagram", placeholder: "https://instagram.com/seusite" },
                  { key: "youtube" as const, icon: "ri-youtube-line", label: "YouTube", placeholder: "https://youtube.com/@seucanal" },
                  { key: "tiktok" as const, icon: "ri-tiktok-line", label: "TikTok", placeholder: "https://tiktok.com/@seusite" },
                  { key: "facebook" as const, icon: "ri-facebook-circle-line", label: "Facebook", placeholder: "https://facebook.com/suapagina" },
                ].map((social) => (
                  <FormField key={social.key} label={social.label}>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0"
                        style={{ backgroundColor: draft.primaryColor + "20", color: draft.primaryColor }}
                      >
                        <i className={`${social.icon} text-lg`}></i>
                      </div>
                      <input
                        type="url"
                        value={draft.socialLinks[social.key]}
                        onChange={(e) => handleSocialChange(social.key, e.target.value)}
                        placeholder={social.placeholder}
                        className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                      />
                      {draft.socialLinks[social.key] && draft.socialLinks[social.key] !== "#" && (
                        <a
                          href={draft.socialLinks[social.key]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-orange-500 hover:border-orange-200 transition-colors cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-external-link-line text-sm"></i>
                        </a>
                      )}
                    </div>
                  </FormField>
                ))}
              </div>

              <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 flex gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-amber-500"></i>
                </div>
                <p className="text-sm text-amber-700">
                  Use <strong>#</strong> para ocultar uma rede social do rodapé. Deixe o campo com <strong>#</strong> e o ícone ficará visível mas sem destino.
                </p>
              </div>
            </div>
          )}

          {/* SEO */}
          {activeTab === "seo" && (
            <div className="space-y-6">
              <SectionTitle icon="ri-search-eye-line" title="SEO & Metadados" />
              <p className="text-sm text-gray-500">Essas informações ajudam o Google a entender e indexar seu site corretamente.</p>

              <FormField label="Descrição do Site" hint="Aparece nos resultados de busca do Google (120-160 caracteres recomendado)">
                <textarea
                  value={draft.seoDescription}
                  onChange={(e) => handleChange("seoDescription", e.target.value)}
                  rows={3}
                  maxLength={160}
                  placeholder="Descreva seu site em até 160 caracteres..."
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{draft.seoDescription.length}/160</p>
              </FormField>

              <FormField label="Palavras-chave" hint="Separadas por vírgula (3-5 palavras principais)">
                <input
                  type="text"
                  value={draft.seoKeywords}
                  onChange={(e) => handleChange("seoKeywords", e.target.value)}
                  placeholder="afiliados, produtos, ofertas, reviews"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                />
              </FormField>

              <div className="border-t border-gray-100 pt-6">
                <p className="text-sm font-semibold text-gray-700 mb-3">Preview no Google</p>
                <div className="rounded-xl border border-gray-100 p-5 bg-white">
                  <p className="text-xs text-green-700 mb-1">seusite.com.br</p>
                  <p className="text-lg text-blue-700 font-medium leading-tight mb-1 hover:underline cursor-pointer">
                    {draft.siteName} — {draft.slogan}
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {draft.seoDescription || "Adicione uma descrição para aparecer aqui nos resultados do Google."}
                  </p>
                </div>
              </div>

              <div className="rounded-xl bg-green-50 border border-green-100 p-4 flex gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-line text-green-500"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-green-800 mb-1">Dicas de SEO</p>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Inclua o nome do site na descrição</li>
                    <li>• Use palavras que seus visitantes pesquisariam</li>
                    <li>• Evite repetir as mesmas palavras muitas vezes</li>
                    <li>• Mantenha a descrição entre 120-160 caracteres</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === "monetizacao" && (
            <AdSettingsPanel
              adSettings={draft.adSettings}
              onChange={(next) => handleChange("adSettings", next)}
            />
          )}
        </div>
      </div>

      {confirmResetOpen && (
        <ConfirmDialog
          title="Restaurar Padrão"
          message="Deseja realmente restaurar padrão?"
          confirmLabel="Sim"
          cancelLabel="Não"
          onConfirm={confirmReset}
          onCancel={() => setConfirmResetOpen(false)}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-xl z-50">
          <i className="ri-checkbox-circle-line text-green-400 mr-2"></i>
          {toast}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-50">
        <i className={`${icon} text-orange-500 text-base`}></i>
      </div>
      <h2 className="text-base font-bold text-gray-800">{title}</h2>
    </div>
  );
}

function FormField({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function ColorPicker({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <div className="relative">
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5 bg-white"
          />
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(v);
          }}
          className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono focus:outline-none focus:border-orange-400 transition-colors"
          maxLength={7}
        />
      </div>
      <p className="text-xs text-gray-400 mt-1">{hint}</p>
    </div>
  );
}